/**
 * First-touch marketing/path attribution shared across the landing sites
 * (callout.jp, heycallout.com) and the app (app.callout.jp, app.heycallout.com).
 *
 * Attribution is persisted in a first-party cookie scoped to the parent domain
 * (`.callout.jp` or `.heycallout.com`) so it survives the landing -> app
 * subdomain hop. The cookie is
 * written once (first-touch) and never overwritten. On direct-to-app traffic
 * (no prior landing visit) the app itself seeds the cookie from its own URL.
 *
 * The vanilla-JS twin of this module lives at `landing/attribution.js` and MUST
 * use the same cookie name and field shape.
 */

export interface Attribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  gclid?: string;
  fbclid?: string;
  referrer?: string;
  landingPage?: string;
  firstSeenAt?: string;
}

const COOKIE_NAME = 'co_attr';
const COOKIE_MAX_AGE_DAYS = 90;

/** URL query param -> Attribution field. */
const PARAM_MAP: Record<string, keyof Attribution> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
  gclid: 'gclid',
  fbclid: 'fbclid',
};

const SITE_DOMAINS = ['callout.jp', 'heycallout.com'];

function cookieDomain(): string | null {
  const host = window.location.hostname;
  for (const domain of SITE_DOMAINS) {
    if (host === domain || host.endsWith('.' + domain)) {
      return '.' + domain;
    }
  }
  // Host-only cookie for localhost / preview domains.
  return null;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string): void {
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  const domain = cookieDomain();
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
  if (domain) cookie += `; domain=${domain}`;
  if (window.location.protocol === 'https:') cookie += '; Secure';
  document.cookie = cookie;
}

/**
 * A URL without its fragment. The fragment can carry what a visitor typed on
 * a landing page (see prefill.ts), which has no place in attribution.
 */
function withoutFragment(url: string): string {
  const i = url.indexOf('#');
  return i === -1 ? url : url.slice(0, i);
}

/** Read the stored first-touch attribution, or null if none is set. */
export function getStoredAttribution(): Attribution | null {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return null;
  let attr: Attribution;
  try {
    attr = JSON.parse(raw) as Attribution;
  } catch {
    return null;
  }
  // Cookies written before landingPage dropped the fragment: clean them.
  if (attr && typeof attr.landingPage === 'string' && attr.landingPage.includes('#')) {
    attr.landingPage = withoutFragment(attr.landingPage);
    try {
      writeCookie(COOKIE_NAME, JSON.stringify(attr));
    } catch {
      // Ignore cookie write failures (e.g. storage disabled).
    }
  }
  return attr;
}

/** Build an attribution snapshot from the current page's URL and referrer. */
function attributionFromCurrentPage(): Attribution {
  const params = new URLSearchParams(window.location.search);
  const attr: Attribution = {};
  for (const [param, field] of Object.entries(PARAM_MAP)) {
    const value = params.get(param);
    if (value) attr[field] = value;
  }
  attr.landingPage = withoutFragment(window.location.href);
  const ref = document.referrer;
  if (ref && !SITE_DOMAINS.some((domain) => ref.includes(domain))) {
    attr.referrer = ref;
  }
  attr.firstSeenAt = new Date().toISOString();
  return attr;
}

/**
 * Ensure a first-touch attribution cookie exists (seeding from the current page
 * for direct-to-app traffic), and return the stored value.
 *
 * First-touch: if a cookie already exists it is returned unchanged.
 */
export function captureAttribution(): Attribution {
  const existing = getStoredAttribution();
  if (existing) return existing;

  const attr = attributionFromCurrentPage();
  try {
    writeCookie(COOKIE_NAME, JSON.stringify(attr));
  } catch {
    // Ignore cookie write failures (e.g. storage disabled).
  }
  return attr;
}
